import { http } from './http-client';
import type { SportCode } from './sports-api';
import {
  type CreatePlaceCommand,
  type PlaceDto,
  type RangeCourtDto,
  type ResourceDto,
  ResourceType,
  type ScheduleBookingDto,
  type ScheduleCourtDto,
  type ScheduleGridResponse,
  type ScheduleRangeResponse,
  type UpdatePlaceBody,
} from './generated';

export type { RangeCourtDto, ScheduleBookingDto, ScheduleCourtDto };
export type ScheduleGridDto = ScheduleGridResponse;
export type ScheduleRangeDto = ScheduleRangeResponse;

export interface PlaceOption {
  id: string;
  name: string;
  sport: SportCode | null;
  sportTypes: SportCode[];
  placeType: ResourceType;
  type: 'indoor' | 'outdoor';
  sortOrder: number;
}

export function toPlaceOptions(rows: ResourceDto[] | null | undefined): { items: PlaceOption[]; total: number } {
  const items: PlaceOption[] = (rows ?? [])
    .filter((r) => r.isActive !== false)
    .map((r, i) => {
      const sportTypes = (r.sportTypes ?? []).map((s) => String(s).toLowerCase() as SportCode);
      return {
        id: r.id,
        name: r.name,
        sport: sportTypes[0] ?? null,
        sportTypes,
        placeType: r.type,
        type: r.isIndoor ? 'indoor' : 'outdoor',
        sortOrder: r.sortOrder ?? i,
      };
    });
  return { items, total: items.length };
}

export type BookingStatus =
  | 'reserved'
  | 'confirmed'
  | 'paid'
  | 'completed'
  | 'cancelled'
  | 'noShow'
  | 'blocked'
  | 'held';

export const bookingTitle = (b: ScheduleBookingDto): string =>
  b.client?.clientName || b.block?.title || b.block?.reason || 'Занято';

export const isFitnessPlace = (placeType: ResourceType | null | undefined): boolean =>
  placeType != null && placeType !== ResourceType.COURT && placeType !== ResourceType.EQUIPMENT;

export const isBookableCourt = <T extends { placeType: ResourceType; sport?: string | null }>(
  c: T,
): c is T & { sport: SportCode } => c.placeType === ResourceType.COURT && !!c.sport;

export const scheduleApi = {
  grid(
    date: string,
    params: { sport?: SportCode; includeCancelled?: boolean } = {},
    call?: { signal?: AbortSignal },
  ): Promise<ScheduleGridDto> {
    return http.get<ScheduleGridDto>('/schedule/grid', {
      query: {
        date,
        sport: params.sport,
        includeCancelled: params.includeCancelled ? true : undefined,
      },
      signal: call?.signal,
    });
  },

  range(
    from: string,
    to: string,
    params: { sport?: SportCode; trainerId?: string } = {},
    call?: { signal?: AbortSignal },
  ): Promise<ScheduleRangeDto> {
    return http.get<ScheduleRangeDto>('/schedule/range', {
      query: { from, to, sport: params.sport, trainerId: params.trainerId },
      signal: call?.signal,
    });
  },

  resources(call?: { signal?: AbortSignal }): Promise<{ items: PlaceOption[]; total: number }> {
    return http.get<ResourceDto[]>('/resources', { signal: call?.signal }).then(toPlaceOptions);
  },

  places(params: { type?: ResourceType } = {}, call?: { signal?: AbortSignal }): Promise<ResourceDto[]> {
    return http
      .get<ResourceDto[]>('/resources', { query: { type: params.type }, signal: call?.signal })
      .then((rows) => rows ?? []);
  },

  createPlace(body: CreatePlaceCommand): Promise<PlaceDto> {
    return http.post<PlaceDto>('/resources', body);
  },

  updatePlace(id: string, body: UpdatePlaceBody): Promise<PlaceDto> {
    return http.put<PlaceDto>(`/resources/${id}`, body);
  },

  deleteBlock(id: string): Promise<void> {
    return http.delete<void>(`/schedule/blocks/${id}`);
  },
};
